import { AuthSessionStore } from "../api/auth_session.js";
import { LevelPackService } from "../level_pack/level_pack_service.js";
import { areBundledLevelsAllowed } from "../level_config.js";
import { NetworkManager } from "../network_manager.js";
import { OfflineEconomy } from "./offline_economy.js";
import { OfflineJournal } from "./offline_journal.js";
import { PersistentCache } from "./persistent_cache.js";

const HEART_TICK_MS = 1000;
const BOOTSTRAP_KEY = "bootstrap_complete_v1";

export class OfflineMode
{
  private static started_offline = false;
  private static heart_ticker: number | null = null;

  public static isPlayable(): boolean
  {
    return this.isBootstrapped() && AuthSessionStore.isLoggedIn() && ( LevelPackService.isActive() || areBundledLevelsAllowed() );
  }

  public static canStartOffline(): boolean
  {
    if ( !this.isBootstrapped() || !AuthSessionStore.isLoggedIn() ) return false;
    return LevelPackService.restoreCachedForFastStart() || areBundledLevelsAllowed();
  }

  public static isBootstrapped(): boolean
  {
    return PersistentCache.read<boolean>( BOOTSTRAP_KEY ) === true;
  }

  public static markBootstrapComplete(): void
  {
    if ( this.isBootstrapped() ) return;
    PersistentCache.write( BOOTSTRAP_KEY, true );
    console.info( "[OfflineMode] first online bootstrap complete — offline start is now allowed" );
  }

  public static resetBootstrap(): void
  {
    PersistentCache.remove( BOOTSTRAP_KEY );
  }

  public static enterOfflineStartup(): void
  {
    if ( this.started_offline ) return;
    this.started_offline = true;
    OfflineEconomy.restoreInventoryFromStats();
    OfflineEconomy.applyHeartsRegen();
    this.settleAbandonedAttempt();
    console.info( `[OfflineMode] starting offline with ${OfflineJournal.count()} journaled action(s)` );
  }

  private static settleAbandonedAttempt(): void
  {
    const journal = OfflineJournal.list();
    const last = journal[journal.length - 1];
    if ( last?.type !== "level_start" || last.payload.settled_locally === true ) return;
    OfflineJournal.patchPayload( last.id, { settled_locally: true } );
    const settled = OfflineEconomy.spendHeart();
    console.info( `[OfflineMode] level ${last.payload.level} was abandoned mid-attempt — heart charged locally (${settled.hearts}/${settled.max_hearts})` );
  }

  public static startedOffline(): boolean
  {
    return this.started_offline;
  }

  public static isOffline(): boolean
  {
    return !NetworkManager.isOnline();
  }

  public static isLevelAvailableOffline( level: number ): boolean
  {
    return LevelPackService.getConfig( level ) !== null || areBundledLevelsAllowed();
  }

  public static startHeartRegenTicker(): void
  {
    if ( this.heart_ticker !== null ) return;
    this.heart_ticker = window.setInterval( () =>
    {
      if ( !NetworkManager.isOnline() || OfflineJournal.hasPending() )
        OfflineEconomy.applyHeartsRegen();
    }, HEART_TICK_MS );
  }
}
